/**
 * Política de conteúdo sem 'unsafe-inline' em estilos (achado DEF-023 da rodada de QA): elementos <style> criados por
 * script (o terminal xterm cria os seus) seriam bloqueados. Esta ponte os transforma em folhas construídas
 * (document.adoptedStyleSheets), que a política não bloqueia porque não vêm de marcação. Só no build de produção: em
 * desenvolvimento o Vite injeta o CSS em <style> e não há política.
 */
const sheets = new WeakMap<Node, CSSStyleSheet>();

function adopt(el: HTMLStyleElement) {
  if (sheets.has(el)) return;
  const sheet = new CSSStyleSheet();
  sheets.set(el, sheet);
  const sync = () => {
    try {
      sheet.replaceSync(el.textContent ?? '');
    } catch {
      /* CSS inválido: ignora, como o navegador faria */
    }
  };
  sync();
  new MutationObserver(sync).observe(el, { childList: true, characterData: true, subtree: true });
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
}

function drop(el: Node) {
  const sheet = sheets.get(el);
  if (!sheet) return false;
  sheets.delete(el);
  document.adoptedStyleSheets = document.adoptedStyleSheets.filter((s) => s !== sheet);
  return true;
}

export function installCspStyleBridge() {
  if (typeof document === 'undefined' || !('adoptedStyleSheets' in Document.prototype)) return;
  const proto = Node.prototype;
  const append = proto.appendChild;
  const insert = proto.insertBefore;
  const remove = proto.removeChild;
  proto.appendChild = function <T extends Node>(this: Node, child: T): T {
    if (child instanceof HTMLStyleElement) {
      adopt(child);
      return child;
    }
    return append.call(this, child) as T;
  };
  proto.insertBefore = function <T extends Node>(this: Node, child: T, ref: Node | null): T {
    if (child instanceof HTMLStyleElement) {
      adopt(child);
      return child;
    }
    return insert.call(this, child, ref) as T;
  };
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (drop(child)) return child;
    return remove.call(this, child) as T;
  };
  const elRemove = Element.prototype.remove;
  Element.prototype.remove = function (this: Element) {
    if (!drop(this)) elRemove.call(this);
  };
}
